import axios from 'axios';
import { getSession, logoutUser } from '../store/auth/authActions';

let store;
let refreshPromise = null;
let logoutInProgress = false;

const AUTH_SKIP_REFRESH_PATHS = [
	'/admin/auth/refresh',
	'/admin/auth/email/login',
	'/admin/auth/logout',
	'/admin/auth/forgot/password',
	'/admin/auth/forgot/otp/verify',
	'/admin/auth/forgot/password/reset',
];

export const injectStore = (_store) => {
	store = _store;
};

export const apiErrorResponse = (error) => {
	if (error.response) {
		console.log(error.response.data);
		console.log(error.response.status);
		console.log(error.response.headers);
	} else if (error.request) {
		console.log(error.request);
	} else {
		console.log('Error', error.message);
	}
};

const isAuthUrl = (url) => {
	if (!url) return false;
	const path = String(url).split('?')[0];
	return AUTH_SKIP_REFRESH_PATHS.some((authPath) => path.endsWith(authPath));
};

const shouldSkipRefresh = (config) => {
	if (!config) return true;
	if (config.skipAuthRefresh) return true;
	return isAuthUrl(config.url);
};

const refreshAccessToken = async () => {
	if (!refreshPromise) {
		refreshPromise = store
			.dispatch(getSession())
			.unwrap()
			.finally(() => {
				refreshPromise = null;
			});
	}
	return refreshPromise;
};

const PUBLIC_AUTH_PATHS = ['/login', '/forgot'];

const isPublicAuthPage = () =>
	PUBLIC_AUTH_PATHS.includes(window.location.pathname);

const forceLogout = async ({ redirect = true } = {}) => {
	if (logoutInProgress) return;
	logoutInProgress = true;
	try {
		await store.dispatch(logoutUser()).unwrap();
	} catch {
		// auth slice resets on logout rejection
	}
	if (redirect && !isPublicAuthPage()) {
		window.location.assign('/login');
	} else {
		logoutInProgress = false;
	}
};

const instance = axios.create({
	baseURL: process.env.REACT_APP_BACKEND_DOMAIN,
	withCredentials: true,
	Accept: 'application/json',
});

instance.interceptors.request.use(
	(config) => {
		const accessToken = store.getState().auth.accessToken;
		if (accessToken) {
			config.headers = {
				...config.headers,
				Authorization: `Bearer ${accessToken}`,
			};
		}

		return config;
	},
	(err) => Promise.reject(err),
);

instance.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;
		const status = error.response?.status;

		if (status !== 401 || !originalRequest) {
			return Promise.reject(error);
		}

		if (shouldSkipRefresh(originalRequest)) {
			// Expected when there is no refresh cookie (e.g. login page bootstrap).
			return Promise.reject(error);
		}

		if (originalRequest._retry) {
			await forceLogout();
			return Promise.reject(error);
		}

		try {
			await refreshAccessToken();
			originalRequest._retry = true;
			return instance(originalRequest);
		} catch {
			await forceLogout();
			return Promise.reject(error);
		}
	},
);

export default instance;
